import { useCallback, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { ContactRoundIcon, Save } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import {
  type Activities_Insert_Input,
  type Activities_Updates,
  Activity_Types_Enum_Enum,
  Lead_Agent_Source_Enum_Enum,
  type Lead_Agents_Insert_Input,
} from '@graphql/__generated__/graphql';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { UserSelect } from '@modules/_shared/components/user-select/UserSelect';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { createResultPageReassignment } from '@modules/_shared/utils/leadAgentReassignment';
import {
  OWNER_INITIATED_LEAD_AGENT_SOURCES,
  keepsProspectionAssignmentTask,
} from '@modules/_shared/utils/leadAgentSources';
import {
  canAssignLeadToBrokerTeams,
  getLeadAssignmentIdsToRemove,
} from '@modules/_shared/utils/leadAssignment';
import { GET_LEADS } from '@modules/leads/leadsQueries';
import { useLocale } from '@shared/hooks/locale';
import { fromGlobalId } from '@shared/utils/global-id';

type AssignBrokerDialogProps = {
  leadsIds: string[];
  open: boolean;
  onCancel: () => void;
  isLegacy?: boolean;
};

const ASSIGN_BROKER_WITH_ACTIVITIES = gql(/* GraphQL */ `
  mutation AssignBrokerWithActivities(
    $assignment_activities: [activities_insert_input!]!
    $lead_agents: [lead_agents_insert_input!]!
    $activities_updates: [activities_updates!]!
    $lead_agents_to_remove: [uuid!]!
    $reassignments: [lead_agents_updates!]!
  ) {
    update_activities_many(updates: $activities_updates) {
      affected_rows
    }

    insert_activities(objects: $assignment_activities) {
      affected_rows
      returning {
        id
        lead_id
      }
    }

    insert_lead_agents(
      objects: $lead_agents
      on_conflict: {
        constraint: lead_agents_lead_id_user_id_source_key
        update_columns: [passed_at, stage_id]
      }
    ) {
      affected_rows
      returning {
        id
      }
    }

    delete_lead_agents(where: { id: { _in: $lead_agents_to_remove } }) {
      affected_rows
    }

    update_lead_agents_many(updates: $reassignments) {
      affected_rows
    }
  }
`);

const GET_LEADS_DATA = gql(/* GraphQL */ `
  query GetLeadsData(
    $leads_ids: [uuid!]!
    $broker_id: uuid!
    $owner_initiated_sources: [lead_agent_source_enum_enum!]!
  ) {
    leads(where: { id: { _in: $leads_ids } }) {
      id
      owner_team_id
      lead_agents(where: { source: { _nin: $owner_initiated_sources } }) {
        id
        user_id
      }
    }

    #TODO: The stage should be retrieved from the current pipeline, not the hard coded "brokerage"
    lead_stages(
      where: {
        name: { _eq: "assign" }
        pipeline: { name: { _eq: "brokerage" } }
      }
      limit: 1
    ) {
      id
    }

    team_members: teams_users(
      where: { team: { teams_users: { user_id: { _eq: $broker_id } } } }
    ) {
      team_id
      user_id
    }

    broker_team_memberships: teams_users(
      where: { user_id: { _eq: $broker_id }, deactivated: { _eq: false } }
    ) {
      team_id
    }
  }
`);

const DialogAssignBroker = ({
  leadsIds,
  open,
  onCancel,
  isLegacy,
}: AssignBrokerDialogProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const [brokerId, setBrokerId] = useState<string | null>(null);
  const [error, setError] = useState<null | ErrorLike>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [getLeadsData] = useLazyQuery(GET_LEADS_DATA);
  const [assignBrokerWithActivities] = useMutation(
    ASSIGN_BROKER_WITH_ACTIVITIES,
    {
      onError: setError,
      refetchQueries: [GET_LEADS],
      onCompleted: emitFeedRefresh,
    },
  );

  const handleConfirm = useCallback(async () => {
    if (!brokerId) {
      return;
    }

    setIsLoading(true);

    try {
      // Get the assign stage ID
      const { data } = await getLeadsData({
        variables: {
          leads_ids: isLegacy
            ? leadsIds.map(leadId => fromGlobalId(leadId))
            : leadsIds,
          broker_id: brokerId,
          owner_initiated_sources: [...OWNER_INITIATED_LEAD_AGENT_SOURCES],
        },
      });

      const stage_id = data?.lead_stages[0]?.id;

      if (data == null || data.leads.length === 0) {
        throw new Error('No leads to assign');
      }

      const brokerTeamIds = new Set(
        data.broker_team_memberships.map(membership => membership.team_id),
      );
      if (
        data.leads.some(
          lead =>
            !canAssignLeadToBrokerTeams(lead.owner_team_id, brokerTeamIds),
        )
      ) {
        throw new Error(
          t('The selected broker is not part of every profile lead team.'),
        );
      }

      const assignmentActivities: Activities_Insert_Input[] = data.leads.map(
        ({ id }) => ({
          lead_id: id,
          assigned_to: brokerId,
          activity_type: Activity_Types_Enum_Enum.Assignment,
          done: false,
          due_at: 'now()',
          ...(me?.is_admin ? { created_by: me?.id } : {}),
        }),
      );

      const teamMembers = data.team_members.map(member => member.user_id);
      const teamIds = Array.from(
        new Set(data.team_members.map(member => member.team_id)),
      );

      const activitiesToRefuse = data.leads.map<Activities_Updates>(
        ({ id }) => ({
          where: {
            lead_id: { _eq: id },
            activity_type: { _eq: Activity_Types_Enum_Enum.Assignment },
            done: { _eq: false },
            assigned_to: { _in: teamMembers },
            ...keepsProspectionAssignmentTask({ leadId: id, brokerId }),
          },
          _set: {
            done: true,
            done_at: 'now()',
            success: false,
          },
        }),
      );

      const lead_agents = data.leads.map<Lead_Agents_Insert_Input>(
        ({ id }) => ({
          lead_id: id,
          user_id: brokerId,
          source: Lead_Agent_Source_Enum_Enum.Crm,
          stage_id,
          // Used only in the conflict resolution to reset the passed_at field
          passed_at: null,
        }),
      );

      // Remove lead_agents rows for team members of the new agent.
      const leadAgentsToRemove = data.leads.flatMap(lead =>
        getLeadAssignmentIdsToRemove(lead, brokerId, teamMembers),
      );

      const result = await assignBrokerWithActivities({
        variables: {
          assignment_activities: assignmentActivities,
          activities_updates: activitiesToRefuse,
          lead_agents,
          lead_agents_to_remove: Array.from(new Set(leadAgentsToRemove)),
          reassignments: data.leads.map(({ id }) =>
            createResultPageReassignment({
              leadId: id,
              replacementBrokerId: brokerId,
              replacementTeamIds: teamIds,
            }),
          ),
        },
      });

      if ((result.data?.insert_lead_agents?.affected_rows ?? 0) > 0) {
        onCancel();
      }
    } catch (error) {
      console.error('Error assigning broker:', error);
      const assignmentError =
        error instanceof Error ? error : new Error('Error assigning broker');
      setError(assignmentError);
    }

    setIsLoading(false);
  }, [
    brokerId,
    getLeadsData,
    isLegacy,
    leadsIds,
    assignBrokerWithActivities,
    me?.is_admin,
    me?.id,
    onCancel,
    t,
  ]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onCancel}
        slotProps={{
          paper: {
            style: {
              width: '100%',
              maxWidth: 500,
              margin: '0 auto',
            },
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ContactRoundIcon size={18} />
          {t('Assign this lead')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              pb: 1,
            }}
          >
            {t('Please select a broker to assign these leads to')}
          </DialogContentText>
          <UserSelect
            autoFocus
            onChange={setBrokerId}
            filters={{
              is_broker: { _eq: true },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button onClick={onCancel}>{t('Cancel')}</Button>
          <LoadingButton
            color="primary"
            variant="contained"
            disabled={brokerId == null}
            loadingPosition="start"
            startIcon={<Save />}
            loading={isLoading}
            onClick={handleConfirm}
          >
            {t('Save')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <MutationErrorSnackbar error={error} onClose={() => setError(null)} />
    </>
  );
};

export default DialogAssignBroker;
